import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import Link from '@material-ui/core/Link';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import data from '../../data';

const useStyles = makeStyles((theme) => ({
  aboutPage: {
    margin: '50px 0',
  },
  aboutSection: {
    padding: '50px 0',
    '&::after': {
      content: '',
      clear: 'both',
      display: 'table',
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0,
    },
  },
  aboutTitle: {
    fontFamily: 'Fira sans, sans-serif',
    textTransform: 'uppercase',
    fontSize: '0.9rem',
    lineHeight: '1rem',
    marginBottom: 10,
    color: '#707070',
    fontWeight: '400',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
      lineHeight: '1rem',
    },
  },
  aboutIntro: {
    fontSize: '2.8rem',
    lineHeight: '3rem',
    fontFamily: 'Playfair Display,sans-serif',
    color: '#000',
    marginBottom: 25,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      lineHeight: '2.3rem',
    },
  },
  aboutText: {
    fontSize: '1.5rem',
    lineHeight: '2.2rem',
    fontWeight: '400',
    color: '#000',
    marginBottom: 20,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      lineHeight: '1.8rem',
    },
  },
  button: {
    backgroundColor: '#009844',
    padding: '7px 30px',
    fontWeight: 300,
    textTransform: 'capitalize',
    textDecoration: 'none',
    fontSize: '1.5rem',
    borderRadius: 0,
    color: '#fff',
    cursor: 'pointer',
    display: 'inline-block',
    float: 'right',
    transition: '0.1s transform ease-in, 0s background-color linear',
    '&:hover': {
      backgroundColor: '#006937',
      transition: '0.1s ease-in',
    },
    '&:visited': {
      color: '#fff',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      padding: '7px 20px',
    },
  },
  img: {
    width: '100%',
    height: 'auto',
    mixBlendMode: 'multiply',
    backgroundBlendMode: 'darken',
  },
  howSection: {
    backgroundColor: '#F6F9E6',
    padding: '50px 0 80px 0',
    '& p': {
      fontSize: '1.3rem',
      fontWeight: 400,
      color: '#404040',
    },
    '& img': {
      marginTop: 40,
    },
    [theme.breakpoints.down('sm')]: {
      '& p': {
        fontSize: '1rem',
      },
    },
  },
  sectionTitle: {
    fontSize: '2.8rem',
    marginBottom: 40,
    color: '#404040',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  resourcesSection: {
    padding: '50px 0',
  },
  resourcesTag: {
    textTransform: 'uppercase',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#707070',
  },
  resourcesTitle: {
    fontSize: '2rem',
    fontWeight: '600',
    margin: '10px 0 20px 0',
    color: '#707070',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  resourcesText: {
    marginBottom: 20,
    fontSize: '1.3rem',
    lineHeight: '1.5rem',
    fontWeight: 400,
    color: '#707070',
  },
  link: {
    color: '#009845',
    textDecoration: 'underline',
    '&:hover': {
      color: '#006937',
    },
  },
}));

const About = ({ lang, width }) => {
  const classes = useStyles();
  const preventDefault = (event) => event.preventDefault();

  return (
    <>
      <Box className={classes.aboutPage}>
        <Box className={classes.aboutSection}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12} sm={7}>
                <Typography variant="h2" className={classes.aboutTitle}>
                  {lang === 'jp' ? data.jp.about.title : data.en.about.title}
                </Typography>
                <Typography variant="body1" className={classes.aboutIntro}>
                  {lang === 'jp' ? data.jp.about.subtitle : data.en.about.subtitle}
                </Typography>
                {!isWidthUp('sm', width) && <img src="./assets/about.png" alt="About Qlik" className={classes.img} />}
                <Typography variant="body1" className={classes.aboutText}>
                  {lang === 'jp' ? data.jp.about.text : data.en.about.text}
                </Typography>
                <a className={classes.button} href="https://www.qlik.com/us/products/why-qlik-is-different">
                  {lang === 'jp' ? data.jp.about.linkText : data.en.about.linkText}
                </a>
              </Grid>
              <Grid item xs={12} sm={5} style={{ textAlign: 'center' }}>
                {isWidthUp('sm', width) && <img src="./assets/about.png" alt="About Qlik" className={classes.img} />}
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box className={classes.howSection}>
          <Container maxWidth="lg">
            <Typography variant="h2" className={classes.sectionTitle}>
              {lang === 'jp' ? data.jp.about.howTitle : data.en.about.howTitle}
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  {lang === 'jp' ? data.jp.about.howP1 : data.en.about.howP1}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  {lang === 'jp' ? data.jp.about.howP2 : data.en.about.howP2}
                </Typography>
              </Grid>
            </Grid>
            <img src="./assets/nebula-picasso-enigma-logo.png" alt="" className={classes.img} />
          </Container>
        </Box>

        <Box className={classes.resourcesSection}>
          <Container maxWidth="lg">
            <Typography variant="h2" className={classes.sectionTitle}>
              {lang === 'jp' ? data.jp.about.resourcesTitle : data.en.about.resourcesTitle}
            </Typography>
            <Grid container spacing={4}>
              {
                data[lang].about.resourcesColumns.map((resource) => (
                  <Grid item xs={12} sm={4} key={resource.title}>
                    <Typography variant="body1" className={classes.resourcesTag}>{resource.tag}</Typography>
                    <Typography variant="h4" className={classes.resourcesTitle}>{resource.title}</Typography>
                    <Typography variant="body1" className={classes.resourcesText}>{resource.text}</Typography>
                    <Link href={resource.linkUrl} onClick={preventDefault} className={classes.link}>{resource.linkText}</Link>
                  </Grid>
                ))
              }
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

About.propTypes = {
  lang: PropTypes.string,
  width: PropTypes.string,
};
About.defaultProps = {
  width: 'sm',
  lang: 'en',
};

export default withWidth()(About);
